import { Check } from 'lucide-react';
import type * as React from 'react';

/**
 * The shared checkmark gutter, and the standard row built on it.
 *
 * Its own module because BOTH pickers draw it — the typeahead `Combobox` and
 * the popover `Picker` — and a gutter that is 14px in one control and 16px in
 * the other is exactly the kind of drift nobody files a bug about but everyone
 * notices.
 */

/**
 * Fixed-width slot for the selected-item checkmark. Always occupies space (even
 * when unselected) so labels stay left-aligned across rows and the check reads
 * as a distinct column rather than nudging the label.
 */
export function CheckSlot({ isSelected }: { isSelected: boolean }) {
  return (
    <span className="flex size-3.5 shrink-0 items-center justify-center">
      {isSelected ? <Check className="size-3.5" /> : null}
    </span>
  );
}

/**
 * Standard two-line picker row: a bold primary label in the shared checkmark
 * gutter, with an optional muted secondary line aligned under the label.
 *
 * The `pl-5` on the secondary line keeps it flush with the label rather than
 * the check column: CheckSlot is `size-3.5` (14px) and the label row's
 * `gap-1.5` adds 6px → 20px = `pl-5`. Keeping the offset here (not duplicated
 * per picker) means it tracks CheckSlot if the gutter ever changes. Used by
 * the user / department / skill autocompletes; `secondary` falsy renders just
 * the label line.
 */
export function CheckRow({
  isSelected,
  label,
  secondary,
}: {
  isSelected: boolean;
  label: React.ReactNode;
  secondary?: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <div className="flex min-w-0 items-center gap-1.5">
        <CheckSlot isSelected={isSelected} />
        <span className="truncate font-medium">{label}</span>
      </div>
      {secondary ? (
        <span className="truncate pl-5 text-xs text-muted-foreground">{secondary}</span>
      ) : null}
    </div>
  );
}
